import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { unslugify, slugify } from '../utils/slugify';
import SEO from '../components/SEO';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://api.sofiasurgicals.com';

function InstrumentProduct() {
    const [selectedMaterial, setSelectedMaterial] = useState('Stainless Steel');
    const [showModal, setShowModal] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { name } = useParams();

    const handleMaterialClick = (material) => setSelectedMaterial(material);
    const handleImageClick = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    const stripHtml = (html) => {
        if (!html) return '';
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const getApiData = useCallback(async () => {
        if (!name) {
            setData(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${BASE_URL}/api/single-instupment-product-name/${unslugify(name)}`
            );
            setData(res.data?.data || null);
        } catch (error) {
            console.error('Error fetching instrument product:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [name]);

    useEffect(() => {
        getApiData();
    }, [getApiData]);

    if (loading) {
        return (
            <>
                <SEO title="Loading Instrument..." url={`/instrument-details/${name}`} />
                <p className="text-center mt-5 mb-5 fs-5">Loading product details...</p>
            </>
        );
    }

    if (!data) {
        return (
            <>
                <SEO title="Instrument Not Found" url={`/instrument-details/${name}`} />
                <p className="text-uppercase mb-5 mt-5 text-center fs-2">
                    No product details available for this instrument.
                </p>
            </>
        );
    }

    const {
        image,
        productDetails,
        stainlessDetails,
        titaniumDetails,
        seoTitle,
        metaDescription,
        metaKeywords,
        slug,
    } = data;

    const imageUrl = image ? `${BASE_URL}/${image}` : '';
    const pageTitle = seoTitle || unslugify(name) || 'Product';
    const description = metaDescription || `High quality ${pageTitle} surgical instruments manufactured by Sofia Surgicals India.`;
    const keywords = stripHtml(metaKeywords || '');
    const canonicalUrl = `/instrument-details/${slug || slugify(pageTitle)}`;

    return (
        <>
            <SEO
                title={pageTitle}
                description={description}
                keywords={keywords}
                image={imageUrl}
                url={canonicalUrl}
                type="product"
            />

            {image && productDetails ? (
                <div className='ThumbnailSection' style={{ background: 'linear-gradient(175deg, #CEE5FD 0%, #FFFFFF 100%)' }}>
                    <div className="container ThumbnailSection">
                        <div className="row px-3 px-md-5">
                            <div className="col-12 col-md-6 text-center mb-4 mb-md-0">
                                <img
                                    src={imageUrl}
                                    alt={pageTitle}
                                    className="img-fluid thumbnailImage"
                                    style={{ maxHeight: '350px', width: '90%', cursor: 'pointer' }}
                                    onClick={handleImageClick}
                                    loading="lazy"
                                />
                            </div>
                            <div
                                className="col-12 col-md-6"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(productDetails) }}
                            />
                        </div>

                        {/* Modal Component */}
                        <Modal show={showModal} onHide={handleCloseModal} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Product View</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <img src={imageUrl} alt={pageTitle} className="img-fluid" />
                            </Modal.Body>
                        </Modal>

                        <div className="container pt-5">
                            <div className="row px-3 px-md-5">
                                <div className="col-12 border border-primary rounded pt-2" style={{ background: '#3496FF' }}>
                                    <div className="btn-group" role="group" aria-label="Basic radio toggle button group">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnradio1"
                                            autoComplete="off"
                                            value="Stainless Steel"
                                            checked={selectedMaterial === 'Stainless Steel'}
                                            onChange={() => handleMaterialClick('Stainless Steel')}
                                        />
                                        <label
                                            className="btn"
                                            htmlFor="btnradio1"
                                            style={{
                                                background: selectedMaterial === 'Stainless Steel' ? '#fff' : '#3496FF',
                                                color: selectedMaterial === 'Stainless Steel' ? '#000' : '#fff',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '5px',
                                            }}
                                        >
                                            Stainless Steel
                                        </label>

                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="btnradio"
                                            id="btnradio2"
                                            autoComplete="off"
                                            value="Titanium"
                                            checked={selectedMaterial === 'Titanium'}
                                            onChange={() => handleMaterialClick('Titanium')}
                                        />
                                        <label
                                            className="btn"
                                            htmlFor="btnradio2"
                                            style={{
                                                background: selectedMaterial === 'Titanium' ? '#fff' : '#3496FF',
                                                color: selectedMaterial === 'Titanium' ? '#000' : '#fff',
                                                border: 'none',
                                                padding: '0.5rem 1rem',
                                                borderRadius: '5px',
                                            }}
                                        >
                                            Titanium
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-4 mb-4 px-3 px-md-5">
                                <div className="col-12">
                                    {selectedMaterial === 'Stainless Steel' ? (
                                        stainlessDetails ? (
                                            <div
                                                style={{ maxHeight: '400px', overflowY: 'auto' }}
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stainlessDetails) }}
                                            />
                                        ) : (
                                            <p>Stainless steel details are not available.</p>
                                        )
                                    ) : (
                                        titaniumDetails ? (
                                            <div
                                                style={{ maxHeight: '400px', overflowY: 'auto' }}
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(titaniumDetails) }}
                                            />
                                        ) : (
                                            <p>Titanium details are not available.</p>
                                        )
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

            ) : (
                <p className="text-center mt-5 mb-5 fs-5">No product details available for this instrument.</p>
            )}
        </>
    );
}

export default InstrumentProduct;